import { Injectable, signal } from '@angular/core';
import { CustomReportPreference } from '@models/reporting/CustomReportPreference';
import { ColDef, Column, ColumnState, ExcelExportParams, GridApi } from 'ag-grid-enterprise';
import { SnackbarNotificationService } from 'ngx-lib';

@Injectable({
    providedIn: 'root'
})
export class GridService {
    readonly columnVisibilityState = signal<Record<string, boolean>>({});
    readonly groupingState = signal<ColDef[]>([]);

    private gridApi?: GridApi;

    constructor(private readonly snackbarNotificationService: SnackbarNotificationService) {}

    setGridApi(api: GridApi): void {
        this.gridApi ??= api;
    }

    removeGridApi(): void {
        this.gridApi = undefined;
        this.groupingState.set([]);
        this.columnVisibilityState.set({});
    }

    downloadCSV(filename: string): void {
        if (this.gridApi) {
            this.gridApi.exportDataAsCsv({ fileName: filename });
        }
    }

    downloadExcel(params: ExcelExportParams): void {
        if (this.gridApi) {
            this.gridApi.exportDataAsExcel(params);
        }
    }

    getColsNames(cols: ColDef[]): string[] {
        return cols.flatMap((col) => (col.field ? [col.field] : []));
    }

    getGroupingAvailableCols(cols: ColDef[]): ColDef[] {
        return cols.filter((col: ColDef) => col.enableRowGroup);
    }

    setGroupingState(newOrder: string[]): void {
        this.gridApi?.setRowGroupColumns(newOrder);
    }

    limitGroupingColumns(max: number): void {
        if (!this.gridApi) return;

        const rowGroupColumns = this.gridApi.getRowGroupColumns();

        if (rowGroupColumns.length > max) {
            const allowedGroupingColumns = rowGroupColumns.slice(0, max).map((col) => col.getColId());
            this.gridApi.setRowGroupColumns(allowedGroupingColumns);
            this.snackbarNotificationService.error('Grouping is limited to 3 columns.');
        }
    }

    getGroupingState(): ColDef[] {
        const groupedCols = this.gridApi?.getColumnState().filter((col) => col.rowGroupIndex !== null);
        const parsedCols = this.parseColStateToColDef(groupedCols ?? []);
        const reorderedCols = this.orderColsByIndex(parsedCols);
        return reorderedCols;
    }

    setGroupingStateValue(newOrder: ColDef[]): void {
        this.groupingState.set([...newOrder]);
    }

    getGroupingStateValue(): ColDef[] {
        return this.groupingState();
    }

    getUnlockedCols(cols: ColDef[]): ColDef[] {
        return cols.filter((col: ColDef) => !col.lockVisible);
    }

    getVisibleCols(): Column[] {
        return this.gridApi ? this.gridApi.getAllDisplayedColumns() : [];
    }

    setColumnsVisible(arr: string[], val: boolean): void {
        if (this.gridApi) {
            this.gridApi.setColumnsVisible(arr, val);
        }
    }

    updateColumnVisibilityState(change: Record<string, boolean>): void {
        this.columnVisibilityState.update((state) => ({ ...state, ...change }));
    }

    setColumnVisibilityState(state: Record<string, boolean>): void {
        this.columnVisibilityState.set({ ...state });
    }

    getColumnVisibilityState(): Record<string, boolean> {
        return this.columnVisibilityState();
    }

    getHiddenState(): string[] {
        const currentState = this.columnVisibilityState();
        return Object.keys(currentState).filter((key) => !currentState[key]);
    }

    getGridApi(): GridApi | undefined {
        return this.gridApi;
    }

    clearColumnVisibilityState(): void {
        this.columnVisibilityState.set({});
    }

    getColsOrder(gridSettings?: Record<string, any>[]): string[] | undefined {
        const currentColOrder: string[] = this.gridApi?.getState().columnOrder?.orderedColIds ?? [];
        const isDiffOrder = !!gridSettings?.some((col: any, i: number) => col.field !== currentColOrder[i]);

        return isDiffOrder ? currentColOrder : undefined;
    }

    getCustomReportPreferences(
        customReportName?: string,
        customReportId = 0,
        prevCustomReportPreferences?: CustomReportPreference[]
    ): CustomReportPreference[] | undefined {
        if (!this.gridApi) return;

        const defaultColWidth = 200;
        const map = new Map<string, any>();

        prevCustomReportPreferences?.forEach((col) => {
            map.set(col.columnId, {
                id: col.id,
                columnId: col.columnId,
                customReportId,
                customReportName,
                width: undefined,
                sortDirection: undefined
            });
        });

        this.gridApi.getState().sort?.sortModel.forEach((col) => {
            const prevColData = map.get(col.colId) ?? {};
            map.set(col.colId, {
                ...prevColData,
                id: prevColData.id ?? 0,
                customReportName,
                customReportId,
                columnId: col.colId,
                sortDirection: col.sort
            });
        });

        this.gridApi
            .getState()
            .columnSizing?.columnSizingModel.filter((col) => col.width !== defaultColWidth)
            .forEach((col) => {
                const prevColData = map.get(col.colId) ?? {};
                map.set(col.colId, {
                    ...prevColData,
                    id: prevColData.id ?? 0,
                    customReportName,
                    customReportId,
                    columnId: col.colId,
                    width: col.width
                });
            });

        const customReportPreferences = Array.from(map.values()).filter((col) => col.width || col.sortDirection);

        return customReportPreferences;
    }

    setColsOrder(colsOrder: string[]): void {
        if (!this.gridApi) return;
        this.gridApi.applyColumnState({
            applyOrder: true,
            state: colsOrder.map((col) => ({ colId: col }))
        });
    }

    setCustomReportPreferences(customReportPreferences?: CustomReportPreference[]): void {
        if (!this.gridApi) return;

        this.gridApi.applyColumnState({
            state: customReportPreferences?.map((col) => ({
                colId: col.columnId,
                width: col.width,
                sort: col.sortDirection
            }))
        });
    }

    clearSortFromCols(colIds: string[]): void {
        if (!this.gridApi) return;
        colIds.forEach((col) => {
            this.gridApi?.applyColumnState({
                state: [
                    {
                        colId: col,
                        sort: null
                    }
                ]
            });
        });
    }

    adjustColumnWidths(): void {
        if (!this.gridApi) return;
        setTimeout(() => {
            this.gridApi?.sizeColumnsToFit({ defaultMinWidth: 120 });
        }, 100);
    }

    private parseColStateToColDef(cols: ColumnState[]): ColDef[] {
        const displayedColumns = this.gridApi?.getColumns() ?? [];
        return displayedColumns.map((col) => {
            const colState = cols.find((c) => c.colId === col.getId());
            return {
                field: col.getColDef().field,
                headerName: col.getColDef().headerName,
                rowGroupIndex: colState?.rowGroupIndex ?? null
            } as ColDef;
        });
    }

    private orderColsByIndex(cols: ColDef[]): ColDef[] {
        return cols
            .filter((col) => col.rowGroupIndex !== null)
            .sort((a, b) => (a.rowGroupIndex ?? 0) - (b.rowGroupIndex ?? 0));
    }
}
